/**
 * Gmail OAuth2 authentication service
 * Handles Google OAuth flow and token management
 */

export interface GmailAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string[];
}

export interface GmailTokens {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  scope: string;
  token_type: string;
  expires_at?: number;
}

export class GmailOAuth {
  private config: GmailAuthConfig;

  constructor(config?: Partial<GmailAuthConfig>) {
    this.config = {
      clientId: config?.clientId || import.meta.env.VITE_GOOGLE_CLIENT_ID,
      clientSecret: config?.clientSecret || import.meta.env.VITE_GOOGLE_CLIENT_SECRET,
      redirectUri: config?.redirectUri || import.meta.env.VITE_GOOGLE_REDIRECT_URI,
      scopes: config?.scopes || [
        'https://www.googleapis.com/auth/gmail.readonly',
        'https://www.googleapis.com/auth/gmail.send',
        'https://www.googleapis.com/auth/userinfo.email',
        'https://www.googleapis.com/auth/userinfo.profile'
      ]
    };
  }

  /**
   * Generate OAuth2 authorization URL
   */
  getAuthUrl(): string {
    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scopes.join(' '),
      access_type: 'offline',
      prompt: 'consent'
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  /**
   * Exchange authorization code for access token
   */
  async exchangeCodeForTokens(code: string): Promise<GmailTokens> {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        code,
        client_id: this.config.clientId,
        client_secret: this.config.clientSecret,
        redirect_uri: this.config.redirectUri,
        grant_type: 'authorization_code'
      })
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token exchange failed: ${error}`);
    }

    const tokens: GmailTokens = await response.json();

    // Calculate expiration timestamp
    tokens.expires_at = Date.now() + (tokens.expires_in * 1000);

    return tokens;
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshAccessToken(refreshToken: string): Promise<GmailTokens> {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        refresh_token: refreshToken,
        client_id: this.config.clientId,
        client_secret: this.config.clientSecret,
        grant_type: 'refresh_token'
      })
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Token refresh failed: ${error}`);
    }

    const tokens: GmailTokens = await response.json();
    tokens.expires_at = Date.now() + (tokens.expires_in * 1000);
    tokens.refresh_token = refreshToken; // Keep the original refresh token

    return tokens;
  }

  /**
   * Check if token is expired or about to expire (within 5 minutes)
   */
  isTokenExpired(tokens: GmailTokens): boolean {
    if (!tokens.expires_at) return true;
    return Date.now() >= (tokens.expires_at - 5 * 60 * 1000);
  }

  /**
   * Get valid access token, refreshing if necessary
   */
  async getValidAccessToken(tokens: GmailTokens): Promise<string> {
    if (this.isTokenExpired(tokens) && tokens.refresh_token) {
      const refreshed = await this.refreshAccessToken(tokens.refresh_token);
      // Update stored tokens (caller should save these)
      Object.assign(tokens, refreshed);
      return refreshed.access_token;
    }
    return tokens.access_token;
  }

  /**
   * Revoke access token
   */
  async revokeToken(token: string): Promise<void> {
    await fetch(`https://oauth2.googleapis.com/revoke?token=${token}`, {
      method: 'POST'
    });
  }

  /**
   * Store tokens in localStorage
   */
  saveTokens(tokens: GmailTokens): void {
    localStorage.setItem('gmail_tokens', JSON.stringify(tokens));
  }

  /**
   * Load tokens from localStorage
   */
  loadTokens(): GmailTokens | null {
    const stored = localStorage.getItem('gmail_tokens');
    if (!stored) return null;
    return JSON.parse(stored);
  }

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    localStorage.removeItem('gmail_tokens');
  }
}
